/**
 * Image URL utilities for alt-text fixer
 * Handles Wix media URLs and external URLs
 */

const WIX_STATIC_MEDIA_URL = 'https://static.wixstatic.com/media/';
const WIX_STATIC_SHAPES_URL = 'https://static.wixstatic.com/shapes/';
const HTTP_PREFIX = 'http';
const MEDIA_PREFIX = 'media/';

/**
 * Check if a URL is an external HTTP(S) URL
 */
export const isExternalUrl = url => {
  return url.startsWith(HTTP_PREFIX);
};

/**
 * Check if a file is an SVG based on extension
 */
export const isSVGfile = file => /\.svg$/i.test(file);

/**
 * Removes a prefix from a string if it starts with that prefix
 */
const removePrefix = (value, prefix) => value.startsWith(prefix) ? value.slice(prefix.length) : value;

/**
 * Resolve a Wix media URL to a specific size
 *
 * @param url - The image URL (can be full URL or just media ID)
 * @param width - Desired width
 * @param height - Desired height
 * @returns Resolved URL with size parameters
 */
export const resolveImageUrl = (url, width, height) => {
  if (isExternalUrl(url)) {
    return url;
  }

  // Extract media ID if it's a full URL
  const mediaId = url.includes('/') ? url.split('/').pop() : url;

  // SVG files need to be served from /shapes/ path, not /media/
  if (isSVGfile(mediaId || '')) {
    return `${WIX_STATIC_SHAPES_URL}${mediaId}`;
  }
  return `${WIX_STATIC_MEDIA_URL}${mediaId}/v1/fill/w_${width},h_${height}/${mediaId}`;
};

/**
 * Get the original (full-size) image URL
 *
 * @param url - The image URL (can be full URL or just media ID)
 * @returns Full-size image URL
 */
export const originalImageUrl = url => {
  if (isExternalUrl(url)) {
    return url;
  }

  // Extract media ID if it's a full URL, and remove media/ prefix if present
  let mediaId = url.includes('/') ? url.split('/').pop() : url;
  mediaId = removePrefix(mediaId || '', MEDIA_PREFIX);

  // SVG files need to be served from /shapes/ path
  if (isSVGfile(mediaId)) {
    return `${WIX_STATIC_SHAPES_URL}${mediaId}`;
  }
  return `${WIX_STATIC_MEDIA_URL}${mediaId}`;
};

/**
 * Create default image utils object
 * Can be used when consumer doesn't need custom URL handling
 */
export const createDefaultImageUtils = () => ({
  isExternalUrl,
  isSVGfile,
  originalImageUrl,
  resolveImageUrl
});
//# sourceMappingURL=image-url.js.map